import { supabase } from '../../lib/supabase';

// AS PASTAS QUE A PESSOA CRIA (e a da Nyta) NO MÓDULO ARQUIVOS (04/10/2026).
//
// Estar numa pasta não muda o caminho do arquivo: é uma linha em `arquivos_nas_pastas` (migração
// 20261004030000). O caminho é referência (marcadores da Nyta, links, histórico), e mover pelo
// Storage o trocaria. Quando o arquivo é renomeado ou apagado, um gatilho em `storage.objects` leva a
// linha junto. Só arquivos do balde `arquivos` entram em pasta: os das músicas e dos shows já moram
// na pasta da música ou do show.

// 'lancamento': a pasta de um lançamento (03/11/2026), que nasce e some com ele (gatilho no banco).
export type TipoDaPastaCriada = 'usuario' | 'nyta' | 'lancamento';

export interface PastaCriada {
  id: string;
  nome: string;
  /** `usuario`: a pessoa criou, renomeia e apaga. `nyta`: a "Nyta IA", onde a Nyta guarda o que gera. */
  tipo: TipoDaPastaCriada;
  criadaEm: string;
}

export interface PastasDoPerfil {
  pastas: PastaCriada[];
  /** Em qual pasta está cada arquivo, pelo caminho. Quem não está aqui está na raiz. */
  pastaDe: Record<string, string>;
}

/** O nome da pasta da Nyta. É reservado: ninguém cria outra pasta com ele. */
export const NOME_DA_PASTA_DA_NYTA = 'Nyta IA';

const NOME_MAXIMO = 80;

/** O que a pessoa digitou, pronto para ser nome de pasta (uma linha, sem espaço sobrando). */
export const nomeDaPasta = (bruto: string): string => bruto.replace(/\s+/g, ' ').trim().slice(0, NOME_MAXIMO);

/** Um nome que não pode virar pasta, e por quê; nulo quando pode. */
export function problemaNoNomeDaPasta(nome: string, existentes: Array<Pick<PastaCriada, 'id' | 'nome'>>, ignorar?: string): string | null {
  const limpo = nomeDaPasta(nome);
  if (!limpo) return 'Escreva o nome da pasta.';
  if (limpo.toLowerCase() === NOME_DA_PASTA_DA_NYTA.toLowerCase()) return `"${NOME_DA_PASTA_DA_NYTA}" é a pasta da Nyta. Escolha outro nome.`;
  if (existentes.some((p) => p.id !== ignorar && p.nome.trim().toLowerCase() === limpo.toLowerCase())) return 'Já existe uma pasta com esse nome.';
  return null;
}

/** O erro do banco dito para a pessoa (nunca a mensagem crua, que vem em inglês). */
function frase(erro: { code?: string; message?: string } | null): Error {
  if (erro?.code === '23505') return new Error('Já existe uma pasta com esse nome.');
  if (erro?.code === '42501' || /row-level security/i.test(erro?.message ?? '')) {
    return new Error('Você não pode mudar as pastas deste perfil.');
  }
  return new Error('Não deu para salvar a pasta agora. Tente de novo.');
}

export async function pastasDoPerfil(artistId: string): Promise<PastasDoPerfil> {
  const [pastas, dentro] = await Promise.all([
    supabase.from('pastas_do_perfil').select('id, nome, tipo, criada_em').eq('artist_id', artistId),
    supabase.from('arquivos_nas_pastas').select('caminho, pasta_id').eq('artist_id', artistId),
  ]);
  if (pastas.error) throw pastas.error;
  if (dentro.error) throw dentro.error;
  return {
    pastas: (pastas.data ?? []).map((p: { id: string; nome: string; tipo: string; criada_em: string }) => ({
      id: p.id, nome: p.nome, tipo: p.tipo === 'nyta' || p.tipo === 'lancamento' ? p.tipo : 'usuario', criadaEm: p.criada_em,
    })),
    pastaDe: Object.fromEntries((dentro.data ?? []).map((l: { caminho: string; pasta_id: string }) => [l.caminho, l.pasta_id])),
  };
}

export async function criarPasta(artistId: string, nome: string): Promise<PastaCriada> {
  const { data, error } = await supabase
    .from('pastas_do_perfil')
    .insert({ artist_id: artistId, nome: nomeDaPasta(nome), tipo: 'usuario' })
    .select('id, nome, tipo, criada_em')
    .single();
  if (error || !data) { console.error('[pastas] criar:', error); throw frase(error); }
  return { id: data.id, nome: data.nome, tipo: 'usuario', criadaEm: data.criada_em };
}

export async function renomearPasta(id: string, nome: string): Promise<void> {
  const { error } = await supabase.from('pastas_do_perfil').update({ nome: nomeDaPasta(nome) }).eq('id', id).eq('tipo', 'usuario');
  if (error) { console.error('[pastas] renomear:', error); throw frase(error); }
}

/** Apaga a PASTA. Os arquivos dela continuam no perfil, soltos na raiz. */
export async function apagarPasta(id: string): Promise<void> {
  const { error } = await supabase.from('pastas_do_perfil').delete().eq('id', id).eq('tipo', 'usuario');
  if (error) { console.error('[pastas] apagar:', error); throw frase(error); }
}

/** Põe o arquivo numa pasta (ou na raiz, com `null`). Só para arquivos do balde `arquivos`. */
export async function moverParaPasta(artistId: string, caminho: string, pastaId: string | null): Promise<void> {
  if (!pastaId) {
    const { error } = await supabase.from('arquivos_nas_pastas').delete().eq('caminho', caminho);
    if (error) { console.error('[pastas] tirar da pasta:', error); throw frase(error); }
    return;
  }
  const { error } = await supabase
    .from('arquivos_nas_pastas')
    .upsert({ caminho, artist_id: artistId, pasta_id: pastaId, movido_em: new Date().toISOString() }, { onConflict: 'caminho' });
  if (error) { console.error('[pastas] mover:', error); throw frase(error); }
}
